import { InitiativeType } from "~/ui-rtk/api/types/custom-attribution-enums"
import {
  ColorConfig,
  getColorConfigForFamily,
  PREDEFINED_TYPE_FAMILY,
} from "~/ui-rtk/constants/initiative-type-colors"

/**
 * High-level grouping that determines how an initiative type is optimized.
 * Demand Generation is measured against Brand ROAS; Demand Capture against
 * direct-response ROAS.
 */
export enum InitiativeCategory {
  DEMAND_CAPTURE = "DEMAND_CAPTURE",
  DEMAND_GENERATION = "DEMAND_GENERATION",
}

/**
 * Metadata for a company's custom initiative type, as returned by
 * `GET /custom-attribution/initiative-types`. Passed into the resolvers below
 * so stored labels/categories/colors win over the generic CUSTOM_ formatting.
 */
export interface CustomInitiativeTypeMeta {
  value: string
  label: string
  category: InitiativeCategory
  color: string | null
  isCustom?: boolean
}

function findCustom(
  value: string,
  customTypes?: CustomInitiativeTypeMeta[],
): CustomInitiativeTypeMeta | undefined {
  return customTypes?.find(c => c.value === value)
}

/**
 * Single source of truth for user-facing initiative type display names.
 * DB/enum values are unchanged — these are display-only labels.
 */
export const INITIATIVE_TYPE_LABELS: Record<InitiativeType, string> = {
  [InitiativeType.DEMAND_CAPTURE_ACQUISITION]:
    "Demand Capture: Cold Prospecting",
  [InitiativeType.DEMAND_CAPTURE_RETARGETING]:
    "Demand Capture: Warm Prospecting",
  [InitiativeType.DEMAND_CAPTURE_BROAD]:
    "Demand Capture: Lukewarm/Broad Prospecting",
  [InitiativeType.DEMAND_GENERATION]: "Demand Generation: Engagement",
  [InitiativeType.DEMAND_GENERATION_2]: "Demand Generation: Traffic",
  [InitiativeType.RETENTION]: "Demand Capture: Retention",
  [InitiativeType.BRANDED_SEARCH]: "Demand Capture: Branded Search",
  [InitiativeType.NON_BRAND_SEARCH]: "Demand Capture: Non Brand Search",
  [InitiativeType.PMAX]: "Demand Capture: PMAX",
}

/**
 * Category each predefined initiative type belongs to. Drives ROAS basis in the
 * Budget Recommender and the Demand Capture/Generation grouping in the UI.
 */
export const INITIATIVE_TYPE_CATEGORIES: Record<
  InitiativeType,
  InitiativeCategory
> = {
  [InitiativeType.DEMAND_CAPTURE_ACQUISITION]:
    InitiativeCategory.DEMAND_CAPTURE,
  [InitiativeType.DEMAND_CAPTURE_RETARGETING]:
    InitiativeCategory.DEMAND_CAPTURE,
  [InitiativeType.DEMAND_CAPTURE_BROAD]: InitiativeCategory.DEMAND_CAPTURE,
  [InitiativeType.RETENTION]: InitiativeCategory.DEMAND_CAPTURE,
  [InitiativeType.BRANDED_SEARCH]: InitiativeCategory.DEMAND_CAPTURE,
  [InitiativeType.NON_BRAND_SEARCH]: InitiativeCategory.DEMAND_CAPTURE,
  [InitiativeType.PMAX]: InitiativeCategory.DEMAND_CAPTURE,
  [InitiativeType.DEMAND_GENERATION]: InitiativeCategory.DEMAND_GENERATION,
  [InitiativeType.DEMAND_GENERATION_2]: InitiativeCategory.DEMAND_GENERATION,
}

const CUSTOM_PREFIX = "CUSTOM_"

function isPredefinedType(value: string): value is InitiativeType {
  return value in INITIATIVE_TYPE_LABELS
}

/**
 * Format a custom type value for display. Mirrors the backend's
 * `formatCustomDisplayName`: `CUSTOM_MY_TYPE` → "My Type".
 */
function formatCustomDisplayName(value: string): string {
  return value
    .slice(CUSTOM_PREFIX.length)
    .split("_")
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ")
}

/**
 * Resolve any initiative type value (predefined or custom) to its user-facing
 * label. Prefers a stored custom label, then the predefined map, then the
 * generic CUSTOM_ formatting, then the raw value.
 */
export function getInitiativeTypeLabel(
  value: string,
  customTypes?: CustomInitiativeTypeMeta[],
): string {
  if (isPredefinedType(value)) return INITIATIVE_TYPE_LABELS[value]
  const custom = findCustom(value, customTypes)
  if (custom) return custom.label
  if (value.startsWith(CUSTOM_PREFIX)) return formatCustomDisplayName(value)
  return value
}

/**
 * Chart-friendly label that wraps after the category prefix (the colon) so the
 * two halves render on separate lines.
 */
export function getInitiativeTypeChartLabel(value: string): string {
  return getInitiativeTypeLabel(value).replace(": ", ":\n")
}

/**
 * Resolve the category for an initiative type. Predefined types use the static
 * map; custom types use their persisted category when provided, otherwise
 * default to Demand Capture.
 */
export function getInitiativeTypeCategory(
  value: string,
  customTypes?: CustomInitiativeTypeMeta[],
): InitiativeCategory {
  if (isPredefinedType(value)) return INITIATIVE_TYPE_CATEGORIES[value]
  const custom = findCustom(value, customTypes)
  if (custom) return custom.category
  return InitiativeCategory.DEMAND_CAPTURE
}

/**
 * Resolve the static Tailwind color config for an initiative type. Predefined
 * types map through PREDEFINED_TYPE_FAMILY; custom types use their stored color
 * family. Both resolve through the static palette (with a fallback family).
 */
export function getInitiativeTypeColorConfig(
  value: string,
  customTypes?: CustomInitiativeTypeMeta[],
): ColorConfig {
  if (isPredefinedType(value)) {
    return getColorConfigForFamily(PREDEFINED_TYPE_FAMILY[value])
  }
  const custom = findCustom(value, customTypes)
  return getColorConfigForFamily(custom?.color ?? null)
}

/**
 * Map an initiative category to the ROAS basis used by the Budget Recommender:
 * Demand Generation is measured on Brand ROAS, everything else on DR ROAS.
 */
export function getRoasBasisForCategory(
  category: InitiativeCategory,
): "BRAND" | "DR" {
  return category === InitiativeCategory.DEMAND_GENERATION ? "BRAND" : "DR"
}
